import type { Filter, Input } from "@/workflow_api/types/core";
import type { Variable } from "./types";
import {
  CONTEXT_GLOBAL_TYPES,
  isHolderLegalGlobalType,
  mapBackendTypeToVariableType,
  mapExpedientNodeId,
  mapFieldTypeToVariableType,
  mapVariableTypeToBackendType,
} from "./useVariableResolver.helpers";
import {
  resolveGlobalFilterToVariable,
  resolveGlobalVariable,
} from "./useVariableResolver.global";

export { isHolderLegalGlobalType };

export const resolveInputToVariable = (
  input: Input[number],
): Variable | null => {
  if (!input) return null;

  if (input.global_type) {
    return resolveGlobalVariable(input);
  }

  if (!input.source_connections || input.source_connections.length === 0) {
    return null;
  }

  const fieldId = input.source_connections?.[0].field_id;
  const outputName = input.source_connections?.[0].output_name;
  const nodeId = input.source_connections?.[0].source_node_id || "";

  if (outputName && fieldId) {
    // check if it's an expedient field variable by checking the field type
    if (input.source_connections?.[0].field?.type === "expedient") {
      // return expedient node id reference
      return {
        id: `${fieldId}-${outputName}`,
        name: "",
        node_id: `${nodeId}-expedient-${fieldId}`,
        field_id: fieldId,
        output_name: outputName,
        ref_name: "",
        type: mapBackendTypeToVariableType(input.type || ""),
      };
    }
    // expanded form variable
    return {
      id: `${fieldId}-${outputName}`,
      name: "",
      node_id: nodeId,
      field_id: fieldId,
      output_name: outputName,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    };
  }

  if (fieldId) {
    // form variable
    const field = input.source_connections?.[0].field;
    if (!field) {
      return null;
    }
    return {
      id: fieldId,
      name: "",
      ref_name: "",
      field_id: fieldId,
      node_id: nodeId,
      type: mapFieldTypeToVariableType(
        field.type,
        (field?.config?.multiple as boolean) || false,
      ),
    };
  }

  if (outputName) {
    // multiple output variable
    return {
      id: `${nodeId}-${outputName}`,
      name: "",
      node_id: nodeId,
      output_name: outputName,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    };
  }

  if (nodeId) {
    return {
      id: nodeId,
      name: "",
      node_id: nodeId,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    }; // variable from single output node
  }

  return null; //is not a variable input
};

export const resolveVariableToInput = (
  variable: Variable,
  name: string,
): Input[number] | null => {
  variable.node_id = mapExpedientNodeId(variable.node_id);
  if (variable.global_type) {
    if (variable.global_type === "PERSON_TYPE") {
      return {
        name,
        type: mapVariableTypeToBackendType(variable.type),
        value: null,
        global_type: variable.global_type,
      };
    }
    if (variable.global_type === "COMPANY_FIELD") {
      return {
        name,
        type: mapVariableTypeToBackendType(variable.type),
        value: variable.id,
        global_type: variable.global_type,
      };
    }
    if (isHolderLegalGlobalType(variable.global_type)) {
      // COMPANY_FIELD / LEGAL_INFO are distinguished by their value (the id).
      return {
        name,
        type: mapVariableTypeToBackendType(variable.type),
        value: variable.id,
        global_type: variable.global_type,
      };
    }
    if (
      variable.node_id === "global-variables" ||
      CONTEXT_GLOBAL_TYPES.has(variable.global_type)
    ) {
      return {
        name,
        type: mapVariableTypeToBackendType(variable.type),
        value: variable.id,
        global_type: variable.global_type,
      };
    }
    return {
      name,
      type: mapVariableTypeToBackendType(variable.type),
      value: variable.node_id || "",
      global_type: variable.global_type,
    };
  }

  if (variable.output_name || variable.field_id) {
    return {
      name,
      type: mapVariableTypeToBackendType(variable.type),
      connection: variable.node_id,
      value: null,
      ...(variable.field_id && {
        field: variable.field_id,
      }),
      ...(variable.output_name && {
        output_name: variable.output_name,
      }),
    };
  }

  return {
    name,
    type: mapVariableTypeToBackendType(variable.type),
    value: null,
    connection: variable.node_id,
  };
};

export const resolveFilterToVariable = (input: Filter): Variable | null => {
  if (!input) return null;

  if (input.global_type) {
    return resolveGlobalFilterToVariable(input);
  }

  if (!input.connection) {
    return null;
  }

  if (typeof input.connection === "string") {
    return null; // not a variable reference
  }

  const fieldId = input.connection.field_id;
  const outputName = input.connection.output_name;
  const nodeId = input.connection.source_node_id || "";

  if (outputName && fieldId) {
    // expanded form variable
    return {
      id: `${fieldId}-${outputName}`,
      name: "",
      node_id: nodeId,
      field_id: fieldId,
      output_name: outputName,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    };
  }

  if (fieldId) {
    // form variable
    const field = input.connection.field;
    if (!field) {
      // If field details are missing, we can't determine the variable type, so we return empty type and variable selector
      // will search for the variable based on fieldId and nodeId
      // this apply only for data center variables due serialization filter/scope cases

      return {
        id: fieldId,
        name: "",
        ref_name: "",
        field_id: fieldId,
        node_id: nodeId,
        type: "",
      };
    }
    // if field is available, search by field type
    return {
      id: fieldId,
      name: "",
      ref_name: "",
      field_id: fieldId,
      node_id: nodeId,
      type: mapFieldTypeToVariableType(
        field.type,
        (field?.config?.multiple as boolean) || false,
      ),
    };
  }

  if (outputName) {
    // multiple output variable
    return {
      id: `${nodeId}-${outputName}`,
      name: "",
      node_id: nodeId,
      output_name: outputName,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    };
  }

  if (nodeId) {
    return {
      id: nodeId,
      name: "",
      node_id: nodeId,
      ref_name: "",
      type: mapBackendTypeToVariableType(input.type || ""),
    }; // variable from single output node
  }

  return null; //is not a variable input
};

export { getNodeIdForContextVariable } from "./useVariableResolver.helpers";
